import { DiscountType, LineItemDiscount } from "../types/discounts";

/** Round to cents, the precision the backend stores money at. */
export const round2 = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/** The minimal shape stacked-discount math needs from one discount. */
export interface StackableDiscount {
  discount_type: DiscountType | string;
  /** Dollars (Fixed Amount) or percent points (Percentage). */
  amount: number;
}

/**
 * Dollars a single discount takes off `base`, before the line-level cap:
 * Percentage → round2(base × amount / 100); Fixed Amount → amount (once per
 * line, not per unit). Mirrors the backend's per-discount `amount_dollars`.
 */
export const discountDollarsOnBase = (
  base: number,
  discount: StackableDiscount,
): number =>
  discount.discount_type === "Percentage"
    ? round2((base * discount.amount) / 100)
    : discount.amount;

/**
 * Total dollars a stack of discounts takes off a line, mirroring the backend:
 * every percentage is computed against the PRE-discount `base` (they never
 * compound), each fixed amount applies once per line, and the total is capped
 * at the base and floored at 0. Use it only where a client-side preview is
 * needed (e.g. a Quick Sale cart before the transaction exists) — persisted
 * lines carry the backend total in `calc__discount_amount`.
 */
export const calculateStackedDiscount = (
  base: number,
  discounts: StackableDiscount[],
): number => {
  if (base <= 0 || discounts.length === 0) return 0;
  const total = discounts.reduce(
    (sum, discount) => sum + discountDollarsOnBase(base, discount),
    0,
  );
  return round2(Math.max(0, Math.min(total, base)));
};

/**
 * Fields a line item (booked service, product, service package, or a client
 * activity row) may carry for discount math. Every field is optional so the
 * helpers below accept any of those shapes.
 */
export interface DiscountedLineFields {
  unit_price?: number | null;
  quantity?: number | null;
  /** Pre-discount pre-tax line base (unit × qty + add-on deltas). */
  calc__cost_excl_tips_discounts?: number | null;
  /** Backend total discount on the line, already capped at the base. */
  calc__discount_amount?: number | null;
  /** Every discount applied to the line, in application order. */
  discounts?: LineItemDiscount[] | null;
}

/** The line's own pre-discount base: the backend base when present, else
 *  unit_price × quantity. */
export const getLineBase = (line: DiscountedLineFields): number =>
  line.calc__cost_excl_tips_discounts ??
  (line.unit_price ?? 0) * (line.quantity ?? 1);

/** True when the line carries at least one discount. */
export const hasLineDiscount = (line: DiscountedLineFields): boolean =>
  (line.discounts?.length ?? 0) > 0;

const CENT = 0.005;

/**
 * Total dollars discounted off a line.
 *
 * Prefers the backend-authoritative `calc__discount_amount` when it's a number,
 * else computes the stacked total from the line's `discounts[]`.
 *
 * `base` defaults to the line's own pre-discount base. Pass a different base
 * (e.g. an optimistic quantity that hasn't saved yet, or a per-unit rate) to
 * recompute the discount against it — the backend total only describes the
 * persisted base, so it's skipped in that case.
 */
export const getLineDiscountDollars = (
  line: DiscountedLineFields,
  base: number = getLineBase(line),
): number => {
  const isOwnBase = Math.abs(base - getLineBase(line)) < CENT;
  if (isOwnBase && typeof line.calc__discount_amount === "number") {
    return line.calc__discount_amount;
  }
  return calculateStackedDiscount(base, line.discounts ?? []);
};

/**
 * A line's price after all its discounts, computed on `base` (defaults to the
 * line's own pre-discount base). Never below 0.
 */
export const getDiscountedLinePrice = (
  line: DiscountedLineFields,
  base: number = getLineBase(line),
): number => Math.max(0, base - getLineDiscountDollars(line, base));

/**
 * Calculates the discounted price for a service or product item.
 * @param item - The item with price and discount information
 * @param quantity - Optional quantity multiplier (defaults to 1)
 * @returns The discounted price (never below 0)
 */
export const calculateDiscountedItemPrice = (
  item: DiscountedLineFields,
  quantity: number = 1,
): number => {
  // For services, use calc__cost_excl_tips_discounts; for products, use unit_price
  const itemPrice = item.calc__cost_excl_tips_discounts ?? item.unit_price ?? 0;
  return getDiscountedLinePrice(item, itemPrice * quantity);
};

/**
 * Per-unit discounted rate for a per-unit / per-syringe line: each percentage
 * applies to the unit rate as-is and each fixed (line-level) amount is spread
 * across the units — a $50 line discount on 24 units is ~$2.08 off each unit,
 * not $50 off one.
 */
export const calculateDiscountedUnitPrice = (
  line: DiscountedLineFields,
  unitPrice: number,
  quantity: number,
): number => {
  const perUnitStack = (line.discounts ?? []).map((discount) =>
    discount.discount_type === "Percentage" || quantity <= 0
      ? discount
      : { ...discount, amount: discount.amount / quantity },
  );
  return Math.max(
    0,
    unitPrice - calculateStackedDiscount(unitPrice, perUnitStack),
  );
};
